import { Component, Node, ScrollView, _decorator } from 'cc';
import Lobby from '../../../../System/Lobby';
import BasicHistoryRecord from './HistoryRecord/BasicHistoryRecord';
import BaseHistoryRecord from './HistoryRecord/BaseHistoryRecord';
import HistoryRecordTitle from './HistoryRecordTitle';
import ItemGenerator from '../../../../../common/scripts/Toolkit/Components/Misc/ItemGenerator';
import { FeatureActivityEventName } from '../../../Data/Config/FeatureActivityConfig';
import LobbyHistoryModel, { JACKPOT_QUERY_TYPE } from '../../../Data/Model/LobbyHistoryModel';

const { ccclass, property } = _decorator;

@ccclass('HistoryRecordGeneratorMap')
class HistoryRecordGeneratorMap {
    @property(ItemGenerator)
    public readonly single: ItemGenerator = null;

    @property(ItemGenerator)
    public readonly jackpot: ItemGenerator = null;
}

@ccclass('HistoryRecordList')
export default class HistoryRecordList extends Component {
    @property(ScrollView)
    private readonly scrollView: ScrollView = null;

    @property(Node)
    private readonly bottomIndicator: Node = null;

    @property(HistoryRecordTitle)
    private readonly recordTitle: HistoryRecordTitle = null;

    // @property(HistoryRecordTitle)
    // private readonly jackpotRecordTitle: HistoryRecordTitle = null;

    @property(HistoryRecordGeneratorMap)
    private readonly recordGeneratorMap: HistoryRecordGeneratorMap = null;

    @property
    private readonly displayItemCount: number = 10;

    public displayJackpot: boolean = false;
    private _currentMachineName: string = '';
    private _currentMachineID: number = -1;
    private _currentRecordGenerator: ItemGenerator = null;
    private _basicHistoryRecordMap: Map<Node, BasicHistoryRecord> = new Map();
    private _noRecordHint: Node = null;
    private _loadingMark: Node = null;

    public get displayMachineID() {
        return this._currentMachineID;
    }

    public onLoad() {
        this.scrollView.node.on(ScrollView.EventType.SCROLL_TO_BOTTOM, () => Lobby.history.requestForNewData());
    }

    public async onEnable() {
        Lobby.history.on(LobbyHistoryModel.EVENT_TYPE.DATA_LIST_UPDATED, this._updateContent, this);
        Lobby.history.on(LobbyHistoryModel.EVENT_TYPE.TIME_RANGE_CHANGED, this._onRecordDataChanged, this);
        Lobby.history.on(LobbyHistoryModel.EVENT_TYPE.GAME_TYPE_CHANGED, this._onRecordDataChanged, this);
        Lobby.history.on(LobbyHistoryModel.EVENT_TYPE.IS_REQUESTING_DATA_LIST_STATE_CHANGED, this._updateLoadingMarkVisibility, this);

        // determine the record generator to use
        const newCurrentRecordGenerator = this.displayJackpot ? this.recordGeneratorMap.jackpot : this.recordGeneratorMap.single;

        // clear display item if the record generator is changed
        if (newCurrentRecordGenerator !== this._currentRecordGenerator) {
            if (this._currentRecordGenerator) {
                this._currentRecordGenerator.displayItemCount = 0;
            }
            this._currentRecordGenerator = newCurrentRecordGenerator;
        }

        // update the record title
        await Lobby.history.syncDataList();
        // const recordTitle = this.displayJackpot ? this.jackpotRecordTitle : this.recordTitle;
        const { recordTitle } = this;
        recordTitle.node.active = true;
        recordTitle.display(
            // reportPromise.then((reportData) => {
            //     if (this.displayJackpot) {
            //         return reportData.jackpotReport.totalWin;
            //     }
            //     const machineReportData = reportData.tableReports.find((data) => data.tableID === this._currentMachineID);
            //     return machineReportData ? machineReportData.totalWin - machineReportData.totalBet : 0;
            // }),
            Promise.resolve(0),
            this._currentMachineName,
            this._currentMachineID,
        );
        this._updateContent();
        this._scrollToTop();
        if (this._currentRecordGenerator.displayItemCount < this.displayItemCount) {
            Lobby.history.requestForNewData();
        }
    }

    public onDisable() {
        Lobby.history.off(LobbyHistoryModel.EVENT_TYPE.DATA_LIST_UPDATED, this._updateContent, this);
        Lobby.history.off(LobbyHistoryModel.EVENT_TYPE.TIME_RANGE_CHANGED, this._onRecordDataChanged, this);
        Lobby.history.off(LobbyHistoryModel.EVENT_TYPE.GAME_TYPE_CHANGED, this._onRecordDataChanged, this);
        Lobby.history.off(LobbyHistoryModel.EVENT_TYPE.IS_REQUESTING_DATA_LIST_STATE_CHANGED, this._updateLoadingMarkVisibility, this);
        this._collapseAllRecords();
        this.recordTitle.node.active = false;
        // this.jackpotRecordTitle.node.active = false;
    }

    public init(noRecordHint: Node, loadingMark: Node) {
        this._noRecordHint = noRecordHint;
        this._loadingMark = loadingMark;
    }

    public updateDisplayHistoryInfo(machineName: string, machineID: number): void {
        this._currentMachineName = machineName;
        this._currentMachineID = machineID;
        Lobby.activity.send(FeatureActivityEventName.OpenHistoryRecordList, {
            MachineInfo: { MachineName: machineName, MachineID: machineID },
        });
    }

    private _onRecordDataChanged() {
        this._collapseAllRecords();
        this._updateContent();
        this._scrollToTop();
        if (this._currentRecordGenerator.displayItemCount < this.displayItemCount) {
            Lobby.history.requestForNewData();
        }
    }

    private _updateContent() {
        const { dataList, noMoreData } = Lobby.history;
        const showingDataList = dataList.filter((data) => data.machineID === this._currentMachineID);
        this._currentRecordGenerator.displayItemCount = showingDataList.length;
        this._noRecordHint.active = !showingDataList.length;
        this.bottomIndicator.opacity = showingDataList.length && noMoreData ? 255 : 0;

        if (!showingDataList.length) {
            return;
        }

        showingDataList.forEach((data, index) => {
            const historyRecord = this._currentRecordGenerator.items[index].getComponent(BaseHistoryRecord);
            historyRecord.display(data, () => Lobby.history.getDetail(data.transactionID, data.roundID));
        });
    }

    private _updateLoadingMarkVisibility() {
        this._loadingMark.active = Lobby.history.isRequestingDataList;
    }

    private _collapseAllRecords() {
        // jackpot history record is not expandable
        if (this.displayMachineID === JACKPOT_QUERY_TYPE) {
            return;
        }
        this._currentRecordGenerator.items.forEach((item) => {
            let basicHistoryRecord = this._basicHistoryRecordMap.get(item);
            if (!basicHistoryRecord) {
                basicHistoryRecord = item.getComponent(BasicHistoryRecord);
                this._basicHistoryRecordMap.set(item, basicHistoryRecord);
            }
            basicHistoryRecord.isExpand = false;
        });
    }

    private _scrollToTop() {
        this.scrollView.stopAutoScroll();
        this.scrollView.scrollToTop();
    }
}
